import { DateTime } from 'luxon';
import { type Discount, getEndDate } from './Discounts';
import type { TranslationString } from './Language';
import { getBillingOccurenceDescription, getRecurringFeeInCents, type PlanVariant } from './PlanVariant';
import type { Localization } from './Localization';
import i18n from '@/plugins/i18n';
import { EMAIL_REMINDER_BEFORE_END_OF_TRIAL_DURATION } from '@/utilities/Constants';

export interface PaymentPeriod {
	label: TranslationString;
	note?: Localization;
	priceInCents: number;
	reductionPercent?: number;
	startDate?: Date;
	hidePrice?: boolean;
}

export interface PaymentPeriodOptions {
	planVariant: PlanVariant;
	trialEndDate?: Date;
	discount?: Discount;
}

export const getPrice = (paymentPeriod: PaymentPeriod): number => {
	if (paymentPeriod.reductionPercent) {
		return paymentPeriod.priceInCents - (paymentPeriod.priceInCents * (paymentPeriod.reductionPercent / 100));
	}

	return paymentPeriod.priceInCents;
};

export const getDefaultPaymentPeriod = (
	{ planVariant }: PaymentPeriodOptions,
	startDate?: Date,
): PaymentPeriod => {
	const billingOccurence = getBillingOccurenceDescription(planVariant);

	if (startDate) {
		return {
			label() {
				return i18n.global.t('price.priceWithBillingOccurence', { billingOccurence: billingOccurence.name(i18n.global.t, 1) });
			},
			priceInCents: getRecurringFeeInCents(planVariant),
			startDate,
		};
	}

	return {
		label() {
			return i18n.global.t('price.totalWithBillingOccurence', { billingOccurence: billingOccurence.name(i18n.global.t, 1) });
		},
		priceInCents: getRecurringFeeInCents(planVariant),
	};
};

export const getTrialPaymentPeriod = (): PaymentPeriod => ({
	label() {
		return i18n.global.t('price.totalWithBillingOccurence', { billingOccurence: i18n.global.tc('billingOccurence.trial.name') }) + '**';
	},
	note: (t) => t('generic.fromToday'),
	priceInCents: 0,
});

export const getEmailReminderPaymentPeriod = (endOfTrailDate: Date): PaymentPeriod => {
	const formattedDate = DateTime.fromJSDate(endOfTrailDate)
		.minus(EMAIL_REMINDER_BEFORE_END_OF_TRIAL_DURATION)
		.setLocale(i18n.global.locale)
		.toLocaleString({ day: '2-digit', month: '2-digit', year: 'numeric' });
	return {
		label: i18n.global.t('paymenPeriod.emailReminder.title'),
		note: (t) => t('generic.date.at', [formattedDate]),
		priceInCents: 0,
		hidePrice: true,
	};
};

export const getCouponPaymentPeriod = (): PaymentPeriod => ({
	label: i18n.global.t('price.totalWithBillingOccurence'),
	note: (t) => t('generic.today'),
	priceInCents: 0,
});

export const getDiscountPaymentPeriod = (
	{ planVariant, discount }: PaymentPeriodOptions & Required<Pick<PaymentPeriodOptions, 'discount'>>,
	startDate?: Date,
): PaymentPeriod => {
	const billingOccurence = getBillingOccurenceDescription(planVariant);

	return {
		label() {
			return i18n.global.t('price.discountedPriceWithBillingOccurence', { billingOccurence: billingOccurence.name(i18n.global.t, 1) });
		},
		reductionPercent: discount.Effect.ReductionPercent,
		priceInCents: getRecurringFeeInCents(planVariant),
		startDate,
	};
};

export const getPaymentPeriodDurationNote = (paymentPeriods: PaymentPeriod[], index: number): TranslationString | null => {
	const currentPeriod = paymentPeriods[index];

	if (!currentPeriod || !currentPeriod.startDate) {
		return null;
	}

	const nextPeriod = paymentPeriods[index + 1];
	const nextPeriodStartDate = nextPeriod ? nextPeriod.startDate : undefined;
	const formattedStartDate = DateTime.fromJSDate(currentPeriod.startDate)
		.setLocale(i18n.global.locale)
		.toLocaleString({ day: '2-digit', month: '2-digit', year: 'numeric' });

	if (nextPeriodStartDate) {
		const formattedEndDate = DateTime.fromJSDate(nextPeriodStartDate)
			.setLocale(i18n.global.locale)
			.toLocaleString({ day: '2-digit', month: '2-digit', year: 'numeric' });

		return () => i18n.global.t('generic.date.fromTo', [formattedStartDate, formattedEndDate]);
	}

	return () => i18n.global.t('generic.date.from', [formattedStartDate]);
};

export const hasTrial = (options: PaymentPeriodOptions): options is PaymentPeriodOptions & Required<Pick<PaymentPeriodOptions, 'trialEndDate'>> => !!options.trialEndDate;

export const hasDiscount = (options: PaymentPeriodOptions): options is PaymentPeriodOptions & Required<Pick<PaymentPeriodOptions, 'discount'>> => !!options.discount;

export const getPaymentPeriods = (options: PaymentPeriodOptions): PaymentPeriod[] => {
	if (hasTrial(options) && hasDiscount(options)) {
		const trialPeriod = getTrialPaymentPeriod();
		const discountPeriod = getDiscountPaymentPeriod(options, options.trialEndDate);
		const afterDiscountPeriod = getDefaultPaymentPeriod(options, getEndDate(options.discount, discountPeriod.startDate));
		const emailReminderPeriod = getEmailReminderPaymentPeriod(options.trialEndDate);

		return [
			trialPeriod,
			emailReminderPeriod,
			discountPeriod,
			afterDiscountPeriod,
		];
	}

	if (hasTrial(options)) {
		const trialPeriod = getTrialPaymentPeriod();
		const afterTrialPeriod = getDefaultPaymentPeriod(options, options.trialEndDate);
		const emailReminderPeriod = getEmailReminderPaymentPeriod(options.trialEndDate);

		return [
			trialPeriod,
			emailReminderPeriod,
			afterTrialPeriod,
		];
	}

	if (hasDiscount(options)) {
		const discountPeriod = getDiscountPaymentPeriod(options);
		const afterDiscountPeriod = getDefaultPaymentPeriod(options, getEndDate(options.discount));

		if (options.trialEndDate) {
			const emailReminderPeriod = getEmailReminderPaymentPeriod(options.trialEndDate);
			return [
				discountPeriod,
				emailReminderPeriod,
				afterDiscountPeriod,
			];
		}

		return [
			discountPeriod,
			afterDiscountPeriod,
		];
	}

	return [getDefaultPaymentPeriod(options)];
};

export const getFirstChargePriceInCents = (options: Omit<PaymentPeriodOptions, 'trialEndDate'>): number => getPrice(getPaymentPeriods(options)[0]);
